import { useCallback, useEffect, useRef, useState } from "react";
import { searchKboCompanies } from "../api/company";
import type { KboSearchRow } from "../api/company";

export type KboSearchStatus = "idle" | "searching" | "ready" | "error";

/**
 * Minimum characters before a request goes out.
 *
 * Under this, `status` stays "idle" and NOTHING is requested — which is what
 * preserves the picker's "empty box shows the whole linked list" behaviour at zero
 * network cost.
 */
export const MIN_SEARCH_CHARS = 3;

/**
 * Digits required before the backend will run its enterprise-number branch at all.
 *
 * Mirrors `MIN_NUMBER_PREFIX_DIGITS` in `yuso-auth-api/src/services/kboDbService.ts`,
 * which is the ONLY place it can change. Measured there against the full 1 952 120-row
 * register: a 1-digit prefix matches 1 621 754 rows and pushes the optimizer off the
 * index entirely (5-30s); 4 digits caps at 9 337 and stays at the network floor.
 *
 * Used for COPY ONLY — the request still goes out at `MIN_SEARCH_CHARS`, because a
 * 3-character term is a perfectly good NAME prefix. This constant is what lets the UI
 * tell the user why three digits found nothing.
 */
export const MIN_NUMBER_DIGITS = 4;

/**
 * 400ms, not the 350ms a typeahead would normally take.
 *
 * `yuso-auth-api` applies ONE global rate limiter — 300 requests / 15 minutes,
 * `app.use`-wide across `/auth`, `/company`, `/ean` and `/partners` — and never
 * calls `app.set('trust proxy')`. Behind a load balancer `req.ip` is therefore the
 * proxy, so every user may share a single bucket, and a chatty typeahead here could
 * rate-limit a stranger out of LOGIN. The extra 50ms plus the 3-character minimum is
 * the mitigation. Do not lower either without checking the deployed topology.
 */
export const SEARCH_DEBOUNCE_MS = 400;

/** Stable identity, so an "idle" reset cannot cause a render. */
const NO_ROWS: KboSearchRow[] = [];

export interface KboCompanySearch {
  results: KboSearchRow[];
  status: KboSearchStatus;
}

/**
 * Debounced Belgian-register (`kbo`) typeahead.
 *
 * 🔴 LOOP LENS — read `DestinationCompanyPicker.tsx` before editing the dep array.
 * A re-request loop of this class needs BOTH of:
 *   (a) the guard is still true after a failure, AND
 *   (b) a value in the DEP ARRAY oscillates on the failure path.
 *
 * This effect has (a) — a rejected search leaves `term`/`token` exactly as they
 * were, so the guard passes again — and deliberately does NOT have (b). Its deps are
 * `[term, token, run]`: `term` is user input, `token` is redux auth, and `run` is a
 * `useCallback` on `[token]` alone. The effect WRITES `results` and `status`, and
 * NEITHER appears in the guard or in the deps.
 *
 * 🔴 THE FORBIDDEN EDIT: never add `status`, `loading` or `results.length` to that
 * dependency array. `status` goes idle → searching → error with the guard still
 * true, which is byte-for-byte the `portfolioLoading` shape documented at
 * `CustomerSwitchTransfer.tsx:140-158` — an unbounded request flood on the failure
 * path, invisible to every render-once test.
 */
export const useKboCompanySearch = (term: string, token: string | undefined): KboCompanySearch => {
  const [results, setResults] = useState<KboSearchRow[]>(NO_ROWS);
  const [status, setStatus] = useState<KboSearchStatus>("idle");

  /** In-flight abort. Reassigned per run; never read at mount time. */
  const controllerRef = useRef<AbortController | null>(null);
  /**
   * Monotonic request id. Abort alone is not enough: a response can already be
   * resolving when the next keystroke fires, and applying it would show results for
   * a term the user has moved past.
   */
  const seqRef = useRef(0);

  /**
   * 🔴 Reads `controllerRef.current` AT CLEANUP TIME, not in the effect body.
   * Capturing it into a local with `[]` deps binds whatever controller existed at
   * MOUNT — which is `null` — so the unmount would abort nothing at all while every
   * real request registered later. This is the `useGroupPreswitch.ts:64-75` trap,
   * and it has no visible symptom: the request simply outlives the component.
   */
  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    []
  );

  const run = useCallback(
    async (q: string) => {
      if (!token) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const seq = ++seqRef.current;

      setStatus("searching");
      try {
        const rows = await searchKboCompanies(q, token, controller.signal);
        if (seq !== seqRef.current) return;
        setResults(rows.length === 0 ? NO_ROWS : rows);
        setStatus("ready");
      } catch (err) {
        if (controller.signal.aborted) return;
        if (seq !== seqRef.current) return;
        setResults(NO_ROWS);
        setStatus("error");
        console.error("Register search failed:", err);
      }
    },
    [token]
  );

  useEffect(() => {
    const q = term.trim();
    if (!token || q.length < MIN_SEARCH_CHARS) {
      // Below the minimum there is nothing to show and nothing outstanding worth
      // keeping: cancel and fall back to "idle". `NO_ROWS` is a module constant so
      // this is a no-op state write when it is already empty.
      controllerRef.current?.abort();
      seqRef.current += 1;
      setResults(NO_ROWS);
      setStatus("idle");
      return;
    }
    const id = setTimeout(() => {
      void run(q);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term, token, run]);

  return { results, status };
};
